import { Shape, SpriteFlag, Sprites } from "./sprites";

/*
 * Structure glyphs as images, drawn by the map's own sprite shader on a
 * small offscreen canvas, for pages outside the map (How to play): they
 * look exactly as the map draws them.
 */

export interface GlyphSpec {
  shape: Shape;
  /** Fill (a depot's stock, a site's progress), 0..1. */
  fill?: number;
  flags?: number;
  /** Size on the map (home is larger, a field depot smaller). */
  size?: number;
}

/** Cream: no side's colour, readable on the page. */
const INK: [number, number, number] = [0.86, 0.82, 0.72];
const PX = 40;

const cache = new Map<string, string>();

/** A glyph as a data URL (null if WebGL2 isn't there). */
export function glyph(g: GlyphSpec): string | null {
  const key = `${g.shape}:${g.fill ?? 1}:${g.flags ?? 0}:${g.size ?? 0.95}`;
  if (cache.has(key)) return cache.get(key)!;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = PX;
  const gl = canvas.getContext("webgl2", { preserveDrawingBuffer: true, premultipliedAlpha: false });
  if (!gl) return null;
  const sprites = new Sprites(gl);
  gl.viewport(0, 0, PX, PX);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  sprites.begin();
  sprites.push(0, 0, g.size ?? 0.95, g.shape, INK, 1, g.fill ?? 1, g.flags ?? 0);
  // A world unit is 19 px, so a 0.95 glyph nearly fills the canvas.
  sprites.draw([0, 0], 19, [PX, PX], 0, 0);
  const url = canvas.toDataURL();
  gl.getExtension("WEBGL_lose_context")?.loseContext();
  cache.set(key, url);
  return url;
}

export { Shape, SpriteFlag };
